interface LogoProps {
  variant?: 'full' | 'icon' | 'wordmark';
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  className?: string;
  inverted?: boolean;
}

const sizeConfig = {
  sm: { iconSize: 28, fontSize: 20 },
  md: { iconSize: 40, fontSize: 28 },
  lg: { iconSize: 48, fontSize: 34 },
  xl: { iconSize: 56, fontSize: 42 },
  '2xl': { iconSize: 52, fontSize: 38 },
};

const sizeClasses = {
  sm: 'h-8 md:h-10',
  md: 'h-12 md:h-14',
  lg: 'h-14 md:h-16',
  xl: 'h-16 md:h-20',
  '2xl': 'h-14 md:h-16',
};

function CyberBracketIcon({ size = 64, inverted = false }: { size?: number; inverted?: boolean }) {
  // Use the same consistent colors as the main website
  // accentColor is used for the dots and the cursor
  // inverted=true means dark logo on light background
  // inverted=false means light logo on dark background (use white/gradient)
  const accentColor = inverted ? '#0f172a' : '#ffffff';
  const uniqueId = `cyber-${inverted ? 'inv' : 'norm'}`;
  
  return (
    <div 
      className="logo-icon-wrapper"
      style={{ 
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: size,
        height: size,
        filter: inverted ? 'none' : 'drop-shadow(0 0 15px rgba(11, 201, 178, 0.4))',
        flexShrink: 0
      }}
    >
      <svg 
        width={size} 
        height={size} 
        viewBox="0 0 64 64" 
        fill="none" 
        xmlns="http://www.w3.org/2000/svg"
        style={{ overflow: 'visible' }}
      >
        <defs>
          <linearGradient id={`${uniqueId}-cyberGrad`} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={inverted ? '#0f172a' : '#0BC9B2'} />
            <stop offset="100%" stopColor={inverted ? '#334155' : '#FF8A3D'} />
          </linearGradient>
          <linearGradient id={`${uniqueId}-sparkGrad`} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={inverted ? '#0f172a' : '#ffffff'} />
            <stop offset="50%" stopColor={inverted ? '#1e293b' : '#0BC9B2'} />
            <stop offset="100%" stopColor={inverted ? '#334155' : '#FF8A3D'} />
          </linearGradient>
          <filter id={`${uniqueId}-sparkGlow`} x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="1.5" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Left Bracket (<) with pulse animation */}
        <g className="bracket-left">
          <path 
            d="M26 12 L8 32 L26 52" 
            stroke={`url(#${uniqueId}-cyberGrad)`}
            strokeWidth="6" 
            strokeLinecap="round" 
            strokeLinejoin="round"
            fill="none"
          />
          <circle cx="8" cy="32" r="1.5" fill={accentColor} />
        </g>

        {/* Right Bracket (>) with pulse animation */}
        <g className="bracket-right">
          <path 
            d="M38 12 L56 32 L38 52" 
            stroke={`url(#${uniqueId}-cyberGrad)`}
            strokeWidth="6" 
            strokeLinecap="round" 
            strokeLinejoin="round"
            fill="none"
          />
          <circle cx="56" cy="32" r="1.5" fill={accentColor} />
        </g>
        
        {/* The Cursor Element with blink animation */}
        <rect 
          className="logo-cursor"
          x="30" 
          y="20" 
          width="4" 
          height="24" 
          rx="2" 
          fill={accentColor}
        />

        {/* The Spark Element - refined 8-pointed star with gradient and glow */}
        <g className="logo-spark" filter={`url(#${uniqueId}-sparkGlow)`}>
          {/* Main 8-pointed star - alternating long/short points */}
          <path 
            d="M32 18 L33.5 29 L36 26 L34.5 30.5 L44 32 L34.5 33.5 L36 38 L33.5 35 L32 46 L30.5 35 L28 38 L29.5 33.5 L20 32 L29.5 30.5 L28 26 L30.5 29 Z" 
            fill={`url(#${uniqueId}-sparkGrad)`}
            opacity="0.95"
          />
          {/* Inner highlight for depth */}
          <circle cx="32" cy="32" r="2" fill={inverted ? '#0f172a' : 'white'} opacity="0.8" />
        </g>
      </svg>
    </div>
  );
}

export function IntegradynLogo({ 
  variant = 'full', 
  size = 'md', 
  className = '',
  inverted = false
}: LogoProps) {
  const config = sizeConfig[size];
  const textColor = inverted ? '#0f172a' : '#ffffff';
  
  if (variant === 'icon') {
    return (
      <div 
        className={`inline-flex items-center justify-center flex-shrink-0 ${className}`}
        data-testid="logo-icon"
      >
        <CyberBracketIcon size={config.iconSize} inverted={inverted} />
      </div>
    );
  }
  
  if (variant === 'wordmark') {
    return (
      <div 
        className={`inline-flex items-baseline flex-shrink-0 ${className}`}
        data-testid="logo-wordmark"
        style={{ fontFamily: "'Outfit', sans-serif" }}
      >
        <span 
          style={{ 
            fontWeight: 800, 
            fontSize: config.fontSize,
            color: textColor,
            letterSpacing: '-1px',
            lineHeight: 1
          }}
        >
          integradyn
        </span>
        <span 
          style={{ 
            fontWeight: 400, 
            fontSize: config.fontSize,
            background: 'linear-gradient(135deg, #0BC9B2 0%, #FF8A3D 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            marginLeft: '2px',
            lineHeight: 1
          }}
        >
          .ai
        </span>
      </div>
    );
  }
  
  return (
    <div 
      className={`inline-flex items-center flex-shrink-0 gap-3 ${sizeClasses[size]} ${className}`}
      data-testid="logo-full"
      style={{ fontFamily: "'Outfit', sans-serif" }}
    >
      <CyberBracketIcon size={config.iconSize} inverted={inverted} />
      <div className="inline-flex items-baseline">
        <span 
          style={{ 
            fontWeight: 800, 
            fontSize: config.fontSize,
            color: textColor,
            letterSpacing: '-1px',
            lineHeight: 1
          }}
        >
          integradyn
        </span>
        <span 
          style={{ 
            fontWeight: 400, 
            fontSize: config.fontSize,
            background: 'linear-gradient(135deg, #0BC9B2 0%, #FF8A3D 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            marginLeft: '2px',
            lineHeight: 1
          }}
        >
          .ai
        </span>
      </div>
    </div>
  );
}

export function IntegradynFavicon() {
  return (
    <svg
      width="32"
      height="32"
      viewBox="0 0 64 64"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <linearGradient id="favCyberGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#0BC9B2" />
          <stop offset="100%" stopColor="#FF8A3D" />
        </linearGradient>
        <linearGradient id="favSparkGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#ffffff" />
          <stop offset="60%" stopColor="#0BC9B2" />
          <stop offset="100%" stopColor="#FF8A3D" />
        </linearGradient>
      </defs>
      
      <rect width="64" height="64" rx="12" fill="#020617" />
      
      <path 
        d="M26 14 L10 32 L26 50" 
        stroke="url(#favCyberGrad)" 
        strokeWidth="5" 
        strokeLinecap="round" 
        strokeLinejoin="round" 
        fill="none"
      />
      <circle cx="10" cy="32" r="1.5" fill="white" />
      
      <path 
        d="M38 14 L54 32 L38 50" 
        stroke="url(#favCyberGrad)" 
        strokeWidth="5" 
        strokeLinecap="round" 
        strokeLinejoin="round" 
        fill="none"
      />
      <circle cx="54" cy="32" r="1.5" fill="white" />
      
      <rect x="30" y="22" width="4" height="20" rx="2" fill="white" />
      
      {/* Refined 8-pointed spark for favicon */}
      <path 
        d="M32 20 L33 28 L35 26 L34 30 L42 32 L34 34 L35 38 L33 36 L32 44 L31 36 L29 38 L30 34 L22 32 L30 30 L29 26 L31 28 Z" 
        fill="url(#favSparkGrad)" 
        opacity="0.9"
      />
      <circle cx="32" cy="32" r="1.5" fill="white" opacity="0.8" />
    </svg>
  );
}

export default IntegradynLogo;
